import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

/** La largeur retenue tant qu'on n'a rien mesuré (premier rendu, tests sans mise en page). */
const LARGEUR_PAR_DEFAUT = 640;

/**
 * La largeur d'un conteneur, suivie quand l'écran se redimensionne : les
 * dessins SVG se calculent à cette largeur, à l'échelle un pour un, plutôt
 * que de s'étirer et de déformer le texte.
 */
export function useLargeur<T extends HTMLElement>(
  finDeLigne = false,
): [RefObject<T | null>, number] {
  const reference = useRef<T | null>(null);
  const [largeur, setLargeur] = useState(LARGEUR_PAR_DEFAUT);

  useEffect(() => {
    const element = reference.current;
    if (!element) return;
    const mesurer = (): void => {
      const mesuree = element.clientWidth;
      if (mesuree > 0) setLargeur(Math.round(mesuree));
    };
    mesurer();
    if (typeof ResizeObserver === 'undefined') return;
    const observateur = new ResizeObserver(mesurer);
    observateur.observe(element);
    return () => observateur.disconnect();
  }, []);

  // Un contenu plus large que son conteneur défile : on arrive sur sa fin (le mois le plus récent).
  useEffect(() => {
    const element = reference.current;
    if (finDeLigne && element) element.scrollLeft = element.scrollWidth;
  }, [finDeLigne, largeur]);

  return [reference, largeur];
}
